// components/AddBillModal.jsx - Updated to use budgets
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { showToast } from './Toast';

const AddBillModal = ({ item, budgets, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    budget_id: '',
    frequency: 'monthly',
    next_due_date: '',
    auto_pay: false,
    payment_account_id: '',
    notes: ''
  });
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    // Fetch accounts for autopay (including credit cards from debts)
    const fetchAccounts = async () => {
      try {
        const [accountsResponse, debtsResponse] = await Promise.all([
          axios.get(`${API_BASE_PATH}/accounts`),
          axios.get(`${API_BASE_PATH}/debts`)
        ]);

        const regularAccounts = (accountsResponse.data || [])
          .filter(a => a.is_active)
          .map(a => ({ ...a, source: 'account' }));

        const creditCardAccounts = (debtsResponse.data || [])
          .filter(d => d.type === 'credit_card')
          .map(d => ({
            id: d.id,
            name: d.name,
            type: 'credit_card',
            current_balance: d.current_balance,
            is_active: true,
            source: 'debt'
          }));

        // Combine and ensure unique IDs (prioritize regular accounts over debts if there's a collision)
        const allAccounts = [...regularAccounts, ...creditCardAccounts];
        const uniqueAccounts = allAccounts.filter((account, index, self) =>
          index === self.findIndex((a) => a.id === account.id)
        );

        setAccounts(uniqueAccounts);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };

    fetchAccounts();
  }, []);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        amount: item.amount || '',
        budget_id: item.budget_id || '',
        frequency: item.frequency || 'monthly',
        next_due_date: item.next_due_date || '',
        auto_pay: item.auto_pay || false,
        payment_account_id: item.payment_account_id || '',
        notes: item.notes || ''
      });
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast('Please enter a bill name', 'error');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    if (formData.auto_pay && !formData.payment_account_id) {
      showToast('Please select a payment account for autopay', 'error');
      return;
    }

    if (formData.auto_pay && !formData.next_due_date) {
      showToast('Please set a next due date for autopay', 'error');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        budget_id: formData.budget_id || null,
        frequency: formData.frequency,
        next_due_date: formData.next_due_date || null,
        auto_pay: formData.auto_pay,
        payment_account_id: formData.auto_pay ? formData.payment_account_id : null,
        notes: formData.notes.trim() || null
      };

      if (item?.id) {
        await axios.put(`${API_BASE_PATH}/bills/${item.id}`, payload);
        showToast('Bill updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE_PATH}/bills`, payload);
        showToast('Bill created successfully', 'success');
      }

      onSave();
    } catch (error) {
      console.error('Error saving bill:', error);
      showToast(error.response?.data?.message || 'Failed to save bill', 'error');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <i className="fas fa-file-invoice"></i>
            {item ? 'Edit Bill' : 'Add New Bill'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">
                Bill Name <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                className="form-control"
                placeholder="e.g., Electric Bill, Internet, Rent"
                value={formData.name}
                onChange={handleChange}
                autoFocus
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">
                  Amount <span style={{ color: 'var(--danger-color)' }}>*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Next Due Date</label>
                <input
                  type="date"
                  name="next_due_date"
                  className="form-control"
                  value={formData.next_due_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Budget (Optional)</label>
                <select
                  name="budget_id"
                  className="form-control"
                  value={formData.budget_id}
                  onChange={handleChange}
                >
                  <option value="">None - Assign when paid</option>
                  {budgets && budgets.map(budget => (
                    <option key={budget.id} value={budget.id}>
                      {budget.name}
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                  Default budget for bill payments
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Frequency</label>
                <select
                  name="frequency"
                  className="form-control"
                  value={formData.frequency}
                  onChange={handleChange}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="auto_pay"
                  checked={formData.auto_pay}
                  onChange={handleChange}
                  style={{ width: 'auto' }}
                />
                <span>Auto-pay enabled</span>
              </label>
              <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                Automatically creates transactions for upcoming bill payments
              </small>
            </div>

            {formData.auto_pay && (
              <div className="form-group">
                <label className="form-label">
                  Payment Account <span style={{ color: 'var(--danger-color)' }}>*</span>
                </label>
                <select
                  name="payment_account_id"
                  className="form-control"
                  value={formData.payment_account_id}
                  onChange={handleChange}
                  required={formData.auto_pay}
                >
                  <option value="">Select Account</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type})
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                  Account that will be charged for autopay transactions
                </small>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                placeholder="Additional notes about this bill (optional)"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-save"></i>
              {item ? 'Update Bill' : 'Create Bill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBillModal;